import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { 
  ArrowRight, 
  Heart, 
  Users, 
  Sparkles, 
  DollarSign, 
  Briefcase, 
  Scale, 
  RefreshCw,
  Video,
  MapPin,
  Calendar,
  Clock,
  Globe,
  ChevronDown,
  ChevronUp,
  Play,
  FileText,
  Headphones,
  BookOpen,
  MessageCircle,
  Check,
  Monitor,
  UserCircle,
  Pause,
  Share2,
  Volume2
} from 'lucide-react';

// =============================================================================
// ===== CONFIGURATION =====
// =============================================================================

const WHATSAPP_PHONE = '5219983032313'; // +52 998 303 2313
const WHATSAPP_PHONE_PIXAN = '529842172816'; // +52 984 217 2816

// Google Maps URLs
const GOOGLE_MAPS_CANCUN = 'https://maps.app.goo.gl/G2JL5nKRZdTESBVXA';
const GOOGLE_MAPS_PLAYA = 'https://www.google.com/maps/search/?api=1&query=JWXR%2B8J%20Playa%20del%20Carmen%2C%20Quintana%20Roo';
const YOUTUBE_CHANNEL = 'https://www.youtube.com/@abbamassage';

// =============================================================================
// ===== WHATSAPP MESSAGES =====
// All prefilled WhatsApp messages - edit here to change any message
// =============================================================================

const WHATSAPP_MESSAGES = {
  // Hero section
  heroMain: 'Hola, me gustaría agendar una sesión de Constelaciones Familiares.',
  heroLearnMore: 'Hola, me gustaría recibir más información sobre las Constelaciones Familiares.',
  
  // Session modality cards
  sessionOnline: 'Hola, me interesa agendar una sesión de Constelaciones Familiares en línea.',
  sessionPresencial: 'Hola, me interesa agendar una sesión individual presencial de Constelaciones Familiares en Cancún.',
  sessionGrupal: 'Hola, me gustaría conocer las próximas fechas de Constelaciones Familiares grupales.',
  
  // Help choosing
  helpChoose: 'Hola, me gustaría recibir orientación para elegir la modalidad de Constelación Familiar más adecuada para mí.',
  
  // Booking section cards
  bookingIndividual: 'Hola, me gustaría agendar una sesión individual presencial de Constelaciones Familiares.',
  bookingOnline: 'Hola, me gustaría agendar una sesión de Constelaciones Familiares en línea.',
  bookingGrupal: 'Hola, me gustaría conocer las próximas fechas de Constelaciones Familiares grupales.',
  
  // Upcoming sessions
  reserveCancun: 'Hola, me interesan las Constelaciones Familiares del 14, 15 o 16 de noviembre en ABBA Massage. ¿Qué modalidades y horarios tienen?',
  reservePlaya: 'Hola, me interesan las Constelaciones Familiares del 11, 12 o 13 de noviembre en PIXAN, Playa del Carmen. ¿Qué modalidades y horarios tienen?',
  
  // General inquiries
  general: 'Hola, tengo algunas preguntas sobre las Constelaciones Familiares.',
  
  // Closing CTA
  closing: 'Hola, me gustaría dar el primer paso y agendar una sesión de Constelaciones Familiares.',
};

// Helper function to generate WhatsApp URL with message
const getWhatsAppUrl = (messageKey) => {
  const message = WHATSAPP_MESSAGES[messageKey] || WHATSAPP_MESSAGES.general;
  const phone = messageKey === 'reservePlaya' ? WHATSAPP_PHONE_PIXAN : WHATSAPP_PHONE;
  return `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
};

// =============================================================================
// ===== IMAGES =====
// =============================================================================

const IMAGES = {
  cony: 'https://customer-assets-eiarnc6j.emergentagent.net/job_b8ee28e8-faf1-498f-b060-6a910d476ac1/artifacts/tu9n6kr6_CONY.png',
  individual: 'https://customer-assets-eiarnc6j.emergentagent.net/job_b8ee28e8-faf1-498f-b060-6a910d476ac1/artifacts/4zqn24sm_mU%C3%91ECOS%20EN%20TRONCO%20SESION%20INDIVIDUAL.png',
  group: 'https://customer-assets-eiarnc6j.emergentagent.net/job_b8ee28e8-faf1-498f-b060-6a910d476ac1/artifacts/vriez1aq_SESION%20EN%20ABBA.png',
  support: 'https://customer-assets-eiarnc6j.emergentagent.net/job_b8ee28e8-faf1-498f-b060-6a910d476ac1/artifacts/5wtlw8zt_APOYO%20EMOCIONAL%20MANO%20SOBRE%20ESPALDA.png',
};

// =============================================================================
// ===== HERO CONTENT =====
// =============================================================================

const HERO_CONTENT = {
  headline: '¿Sientes que hay algo en tu vida que no cambia?',
  subheadline: 'A veces hacemos todo lo posible por cambiar una situación y, aun así, volvemos a vivir conflictos parecidos, relaciones similares o emociones que no entendemos.',
  subheadline2: 'Las Constelaciones Familiares ofrecen una manera diferente de mirar esas situaciones.',
  ctaPrimary: 'Agenda una Sesión',
  ctaSecondary: '¿Qué son las Constelaciones?',
};

// =============================================================================
// ===== INTRODUCTION CONTENT =====
// =============================================================================

const INTRODUCTION_CONTENT = {
  label: 'Introducción',
  title: '¿Qué son las Constelaciones Familiares?',
  paragraphs: [
    'Las Constelaciones Familiares ofrecen un espacio para ver aquello que quizá hasta hoy no habías podido ver o expresar. Muchas personas descubren que, cuando entienden mejor lo que está pasando, también les resulta más fácil dar el siguiente paso.',
    'No buscan decirte qué hacer ni tomar decisiones por ti. Tampoco buscan encontrar culpables. Su propósito es ayudarte a entender mejor lo que estás viviendo para que puedas relacionarte con ello de una manera diferente.',
    'En lugar de enfocarse únicamente en el problema, ayudan a ver el contexto en el que ese problema existe. A veces, cuando logramos ver algo que antes no veíamos, también empezamos a entender nuestra historia de una forma distinta.',
  ],
  quote: '"No buscamos culpables. Buscamos entender."',
};

// =============================================================================
// ===== SITUATIONS CONTENT =====
// =============================================================================

const SITUATIONS_CONTENT = {
  label: 'Aplicaciones',
  title: '¿Qué situaciones pueden trabajarse?',
  subtitle: 'Cada persona llega por una historia diferente. Algunos temas frecuentes son:',
  items: [
    { icon: 'heart', label: 'Relaciones de pareja' },
    { icon: 'users', label: 'Conflictos familiares' },
    { icon: 'sparkles', label: 'Duelos y pérdidas' },
    { icon: 'user', label: 'Autoestima' },
    { icon: 'dollar', label: 'Dinero' },
    { icon: 'briefcase', label: 'Trabajo' },
    { icon: 'scale', label: 'Dificultad para poner límites' },
    { icon: 'refresh', label: 'Patrones que se repiten' },
  ],
  additionalItems: [
    'Salud',
    'Miedos',
    'Culpa',
    'Decisiones importantes',
    'Relación con padres o hijos',
  ],
  note: 'No es necesario que tu situación aparezca en esta lista. Si hay algo que lleva tiempo pesando en tu vida y no has logrado cambiar, puede ser un tema para trabajar.',
};

// =============================================================================
// ===== SESSION MODALITIES (3 CARDS) =====
// These replace the old comparison table
// =============================================================================

const SESSION_MODALITIES = {
  label: 'Modalidades',
  title: '¿Cómo es una sesión?',
  
  cards: [
    {
      id: 'online',
      title: 'Sesión Individual en Línea',
      subtitle: 'Un espacio privado para trabajar desde cualquier lugar y dar continuidad a tu proceso.',
      description: [
        'La sesión en línea permite trabajar una Constelación Familiar con la misma profundidad y acompañamiento que una sesión presencial.',
        'Es ideal para personas que viven fuera de Cancún o México, buscan privacidad o desean dar continuidad a un proceso terapéutico desde este enfoque.',
        'Puede ser especialmente útil cuando alguien atraviesa un duelo, una separación, ansiedad, tristeza, cambios importantes o cualquier situación que probablemente necesite más de una sesión.',
        'Las Constelaciones Familiares pueden utilizarse como una herramienta terapéutica complementaria y combinarse con otros procesos psicológicos o médicos cuando así se requiera.',
      ],
      duration: '90 minutos o un poco más.',
      idealFor: [
        'Personas que viven fuera de Cancún o México.',
        'Personas que buscan privacidad.',
        'Continuar un proceso terapéutico.',
        'Acompañamiento individual desde este enfoque.',
      ],
      buttonText: 'Agendar Sesión en Línea',
      whatsappKey: 'sessionOnline',
      icon: 'globe',
      badge: 'Disponible en todo el mundo',
    },
    {
      id: 'presencial',
      title: 'Sesión Individual Presencial',
      subtitle: 'Un espacio íntimo y completamente enfocado en tu historia.',
      description: [
        'La sesión individual permite trabajar una situación específica dentro de un ambiente privado, tranquilo y seguro.',
        'No necesitas contar toda tu historia.',
        'Se parte de aquello que hoy deseas trabajar y el proceso se desarrolla con respeto, profundidad y privacidad.',
        'Cada sesión tiene un inicio, un desarrollo y un cierre.',
      ],
      duration: '90 minutos o un poco más.',
      idealFor: [
        'Personas que desean privacidad.',
        'Quienes desean comenzar individualmente.',
        'Personas que buscan profundizar un tema.',
      ],
      buttonText: 'Agendar Sesión Presencial',
      whatsappKey: 'sessionPresencial',
      icon: 'user',
      badge: null,
    },
    {
      id: 'grupal',
      title: 'Constelación Familiar Grupal',
      subtitle: 'Un espacio terapéutico donde todos trabajan a favor de todos.',
      description: [
        'Las Constelaciones Grupales se realizan en grupos pequeños de aproximadamente 4 a 10 personas y tienen una duración de 4 horas.',
        'Cada participante puede trabajar un tema personal sin necesidad de contar toda su historia.',
        'En los grupos facilitados por Cony todos constelan. Todos participan. Todos aportan. Todos reciben.',
        'Existe un equilibrio entre dar y recibir.',
        'Muchas personas descubren recursos importantes al representar la historia de otra persona y también al permitir que otros representen la propia.',
        'Después de una Constelación Grupal es posible continuar el proceso mediante sesiones individuales presenciales o en línea.',
      ],
      duration: '4 horas.',
      idealFor: null,
      buttonText: 'Ver Próximas Fechas',
      whatsappKey: 'sessionGrupal',
      icon: 'users',
      badge: null,
    },
  ],
  
  helpChoose: {
    title: '¿No sabes cuál modalidad elegir?',
    description: 'Escríbenos y con gusto te ayudaremos a encontrar la opción más adecuada para ti.',
    buttonText: 'Hablar por WhatsApp',
    whatsappKey: 'helpChoose',
  },
};

// =============================================================================
// ===== THERAPEUTIC APPROACH =====
// =============================================================================

const THERAPEUTIC_APPROACH = {
  label: 'Enfoque',
  title: 'Un enfoque terapéutico complementario',
  paragraphs: [
    'Las Constelaciones Familiares se trabajan a través de una teoría, una filosofía y un método estructurado desarrollado originalmente por Bert Hellinger y enriquecido por las aportaciones de diversos autores y facilitadores a lo largo de los años.',
    'Cony integra las Constelaciones Familiares como una herramienta terapéutica complementaria dentro de su formación en pedagogía, psicología y desarrollo humano.',
    'Este enfoque permite observar las dinámicas familiares y sistémicas desde una perspectiva diferente, abriendo posibilidades de comprensión y movimiento.',
  ],
  disclaimer: 'Las Constelaciones Familiares NO sustituyen el tratamiento psicológico, psiquiátrico o médico cuando estos son necesarios.',
};

// =============================================================================
// ===== MYTHS CONTENT =====
// =============================================================================

const MYTHS_CONTENT = {
  label: 'Desmitificando',
  title: '¿Qué NO son las Constelaciones Familiares?',
  subtitle: 'Es normal tener preguntas antes de asistir a una sesión. Estas son algunas de las dudas más frecuentes.',
  items: [
    {
      question: '¿Son magia?',
      answer: 'No. Las Constelaciones Familiares no buscan explicar la vida desde la magia ni ofrecer soluciones inmediatas. Son un espacio para ver una situación desde una perspectiva diferente y abrir nuevas posibilidades de entendimiento.',
    },
    {
      question: '¿Sustituyen la terapia psicológica?',
      answer: 'No. Las Constelaciones Familiares no sustituyen la atención médica, psicológica o psiquiátrica cuando esta es necesaria. Pueden ser un complemento dentro del proceso de algunas personas, pero nunca reemplazan la atención de un profesional de la salud.',
    },
    {
      question: '¿Necesito creer en algo para asistir?',
      answer: 'No. No es necesario pertenecer a una religión, tener una práctica espiritual o compartir una forma específica de pensar. Lo más importante es llegar con disposición para vivir la experiencia.',
    },
    {
      question: '¿Tengo que revivir momentos difíciles?',
      answer: 'No es el objetivo. El propósito de una Constelación no es hacer que una persona vuelva a sufrir lo que ya vivió, sino ayudarla a ver su historia de una manera diferente.',
    },
  ],
};

// =============================================================================
// ===== FOR YOU CONTENT =====
// =============================================================================

const FOR_YOU_CONTENT = {
  label: '¿Es para ti?',
  title: 'Esta experiencia puede ser para ti si...',
  items: [
    'Sientes que repites situaciones que no has logrado cambiar.',
    'Hay una relación que te cuesta entender.',
    'Estás viviendo un duelo o una pérdida importante.',
    'Quieres tomar una decisión y sientes que algo te detiene.',
    'Hay un tema que lleva mucho tiempo pesando en tu vida.',
    'Simplemente sientes que necesitas mirar las cosas desde otro lugar.',
  ],
  ctaText: 'Agenda una Sesión',
  whatsappKey: 'heroMain',
};

// =============================================================================
// ===== CONY BIOGRAPHY =====
// Source of truth: Cony Trejo BIO (CT).pdf
// Do NOT add information not contained in the original document
// =============================================================================

const CONY_BIOGRAPHY = {
  label: 'Facilitadora',
  title: 'Conoce a Cony Trejo',
  role: 'Consteladora Familiar',
  shortBio: 'Con más de 25 años de experiencia acompañando procesos de desarrollo humano, Cony ha dedicado su vida a ayudar a las personas a entender mejor sus historias familiares.',
  philosophy: 'Su filosofía de trabajo se basa en el respeto profundo por cada historia y en la confianza de que cada persona tiene dentro de sí la sabiduría necesaria para encontrar su camino.',
  ctaText: 'Conocer más sobre Cony',
  
  // Expanded biography - organized in sections for readability
  expandedBio: {
    sectionId: 'cony-bio-expanded',
    title: 'Sobre Cony Trejo',
    
    sections: [
      {
        title: 'El camino',
        paragraphs: [
          'Cony comenzó a facilitar dinámicas grupales enfocadas en el desarrollo humano a los 17 años, inicialmente en prevención de adicciones y promoción de la salud. Más adelante integró la psicología a su trabajo.',
          'Hay quienes no eligen su camino; el camino los elige a ellos. La pedagogía fue el primer paso de una trayectoria construida sobre las emociones, el lenguaje del cuerpo y el inconsciente colectivo, integrando eventualmente estos aprendizajes al servicio de la vida.',
        ],
      },
      {
        title: 'Formación',
        paragraphs: [
          'Su formación en pedagogía y psicología se complementa con herramientas que permiten una comprensión más profunda del ser humano: terapia psicocorporal, biodanza, bioenergética, terapia de sonido, mindfulness, biofísica, tanatología e inteligencia emocional.',
          'No colecciona títulos, sino conocimientos, porque cada aprendizaje abre una nueva manera de entender a las personas.',
        ],
      },
      {
        title: 'El encuentro con las Constelaciones',
        paragraphs: [
          'Las Constelaciones Familiares revelaron la necesidad de abordar emociones atrapadas en el cuerpo que guardaban historias del pasado, de la familia y de los sistemas: cosas no dichas pero cargadas en silencio.',
          'Descubrió que dentro de esas historias hay recursos, fortalezas y aprendizajes capaces de transformar la vida cuando se miran conscientemente.',
          'Su camino hacia las Constelaciones Familiares fue influenciado por el estudio y la exploración de autores como Carl Rogers, Wilhelm Reich, Alexander Lowen, Carl Gustav Jung y Bert Hellinger, integrando sus perspectivas en una forma de acompañamiento profundamente humana y sensible.',
        ],
      },
      {
        title: 'Forma de trabajar',
        paragraphs: [
          'Crea espacios seguros donde las personas pueden mirar su historia con honestidad, descubrir habilidades, reconocer bloqueos y reconectarse con su esencia, dejando atrás máscaras y patrones que ya no necesitan.',
          'Trabaja con amor, pero también con firmeza. Cree profundamente que la constancia y la acción son partes esenciales de cualquier proceso de transformación.',
          'Cada historia merece respeto, privacidad y profundidad. Ayuda a las personas a reconocer y soltar lo que heredaron sin saberlo.',
        ],
      },
      {
        title: 'Trayectoria',
        paragraphs: [
          'Con más de 25 años de experiencia moviéndose entre el aprendizaje y la enseñanza, sus clientes la han llevado a nuevas ciudades, grupos y comunidades.',
          'Ha acompañado procesos de transformación en Acapulco, Veracruz, Puebla, Ciudad de México, Hidalgo, Guadalajara, Playa del Carmen y Cancún.',
          'Trabaja en entornos privados y grupos pequeños donde ocurren cambios reales y profundos. A lo largo de los años, sus clientes han iniciado negocios exitosos, dejado relaciones dañinas, vivido reconciliaciones aparentemente imposibles y tomado decisiones que transformaron completamente sus vidas.',
        ],
      },
      {
        title: 'Filosofía',
        paragraphs: [
          '"La salud integral es la mayor riqueza. El cuerpo indica el movimiento y al reeducar la mente, se camina más ligero."',
          'Acompaña con una calidez y claridad nacidas de años, experiencia y una vocación genuina.',
          'Está convencida de que dentro de cada persona hay una oportunidad de servir. Las señales continúan y el camino es infinito.',
        ],
      },
    ],
  },
};

// =============================================================================
// ===== RESOURCE LIBRARY =====
// Add new resources by duplicating an object in the RESOURCES array
// =============================================================================

// Category configuration - edit labels here for translations
const RESOURCE_CATEGORIES = {
  all: { id: 'all', label: 'Todos', icon: null },
  video: { id: 'video', label: 'Videos', icon: 'video' },
  audio: { id: 'audio', label: 'Audios', icon: 'headphones' },
  meditation: { id: 'meditation', label: 'Meditaciones', icon: 'sparkles' },
  exercise: { id: 'exercise', label: 'Ejercicios', icon: 'heart' },
};

// Resource library section content
const RESOURCE_LIBRARY_CONTENT = {
  label: 'Recursos',
  title: 'Sigue Acompañando tu Proceso',
  subtitle: 'Ejercicios, meditaciones, audios y reflexiones para volver a ellos cada vez que los necesites.',
  emptyState: 'Próximamente agregaremos más recursos.',
  comingSoonLabel: 'Próximamente',
};

// =============================================================================
// ===== RESOURCES ARRAY =====
// To add a new resource, copy an existing object and modify its values
// Supported media types: audio (mp3, ogg, m4a), video (mp4, YouTube, Vimeo)
// =============================================================================

const RESOURCES = [
  {
    id: 'ejercicio-plenitud',
    title: 'Ejercicio para conectar con la plenitud',
    subtitle: null,
    description: 'Un ejercicio breve para detenerte unos minutos, respirar y conectar con aquello que hoy sí está presente en tu vida.',
    category: 'audio', // Options: video, audio, meditation, exercise
    duration: '0:49',
    thumbnail: null,
    mediaUrl: 'https://customer-assets-eiarnc6j.emergentagent.net/job_b8ee28e8-faf1-498f-b060-6a910d476ac1/artifacts/ew36ax6b_WhatsApp%20Audio%202026-07-21%20at%2011.27.49%20AM.ogg',
    mediaType: 'audio', // audio, video, youtube, vimeo
    buttonText: '✨ Comenzar ejercicio',
    featured: true,
    visible: true,
    order: 1,
    comingSoon: false,
  },
  {
    id: 'abrirse-a-lo-nuevo',
    title: 'Abrirse a lo Nuevo',
    subtitle: null,
    description: 'Una reflexión sobre avanzar aunque la vida no siempre se vea como la imaginábamos. Elegir confiar en nuestra capacidad para adaptarnos también puede abrir nuevos caminos.',
    category: 'video',
    duration: '1:22',
    thumbnail: null,
    mediaUrl: 'https://customer-assets-eiarnc6j.emergentagent.net/job_b8ee28e8-faf1-498f-b060-6a910d476ac1/artifacts/wzmp5hld_WhatsApp%20Video%202026-07-21%20at%2011.27.02%20AM.mp4',
    mediaType: 'video',
    buttonText: '✨ Ver reflexión',
    featured: true,
    visible: true,
    order: 2,
    comingSoon: false,
  },
  // =============================================================================
  // ADD NEW RESOURCES BELOW - Copy this template:
  // =============================================================================
  // {
  //   id: 'unique-id-here',
  //   title: 'Resource Title',
  //   subtitle: null,
  //   description: 'Brief description of the resource.',
  //   category: 'video', // Options: video, audio, meditation, exercise
  //   duration: '5:00',
  //   thumbnail: null, // Optional: URL to thumbnail image
  //   mediaUrl: 'URL to media file or YouTube/Vimeo link',
  //   mediaType: 'video', // audio, video, youtube, vimeo
  //   buttonText: '✨ Button text',
  //   featured: false,
  //   visible: true,
  //   order: 3,
  //   comingSoon: false,
  // },
];

// =============================================================================
// ===== FAQ CONTENT =====
// =============================================================================

const FAQ_CONTENT = {
  label: 'Preguntas',
  title: 'Preguntas Frecuentes',
  items: [
    {
      q: '¿Quién puede constelar?',
      a: 'Cualquier persona que sienta que hay algo en su vida que desea comprender mejor o trabajar. No hay requisitos previos ni es necesario tener experiencia.',
    },
    {
      q: '¿Hay edad mínima?',
      a: 'Se recomienda para adultos. En casos de menores, se evalúa individualmente la pertinencia del trabajo.',
    },
    {
      q: '¿Necesito llevar un tema específico?',
      a: 'Es recomendable llegar con una intención o tema que te gustaría explorar, aunque no es obligatorio tenerlo completamente definido.',
    },
    {
      q: '¿Tengo que contar toda mi historia?',
      a: 'No necesariamente. Cada persona comparte únicamente aquello con lo que se siente cómoda. No es necesario contar todos los detalles para participar.',
    },
    {
      q: '¿Qué pasa si me emociono durante la sesión?',
      a: 'Es completamente normal. Las emociones son parte del proceso y serán acompañadas con respeto y cuidado durante toda la sesión.',
    },
    {
      q: '¿Qué ocurre después de constelar?',
      a: 'Cada persona vive el proceso de manera diferente. Algunas sienten alivio inmediato, otras necesitan tiempo para integrar lo vivido. Cony ofrece recomendaciones para los días siguientes.',
    },
    {
      q: '¿Cada cuánto tiempo puedo hacer una Constelación?',
      a: 'No hay una regla fija. Algunas personas hacen una sola sesión, otras regresan cuando sienten que hay algo nuevo que trabajar. Cada proceso tiene su propio ritmo.',
    },
    {
      q: '¿Existe alguna contraindicación?',
      a: 'Las Constelaciones no son recomendables durante crisis agudas de salud mental. En esos casos, es preferible contar primero con acompañamiento especializado.',
    },
  ],
};

// =============================================================================
// ===== UPCOMING SESSIONS =====
// =============================================================================

const UPCOMING_SESSIONS = {
  label: 'Calendario · 11 al 16 de noviembre de 2026',
  title: 'Próximas Sesiones',
  intro: 'Cony Trejo estará en PIXAN, Playa del Carmen, del miércoles 11 al viernes 13 de noviembre, y en ABBA Massage, Cancún, del sábado 14 al lunes 16. Consulta modalidades y horarios directamente con cada sede por WhatsApp.',
  alternative: '¿No puedes asistir en estas fechas?',
  alternativeSubtitle: 'También puedes agendar una sesión individual o en línea.',
  locations: [
    {
      city: 'Playa del Carmen',
      venue: 'Temazcal y Masajes PIXAN',
      schedule: [
        { date: 'Miércoles 11 de noviembre', detail: 'Consulta modalidad y horario con PIXAN por WhatsApp.' },
        { date: 'Jueves 12 de noviembre', detail: 'Consulta modalidad y horario con PIXAN por WhatsApp.' },
        { date: 'Viernes 13 de noviembre', detail: 'Consulta modalidad y horario con PIXAN por WhatsApp.' },
      ],
      ctaDirections: 'Cómo Llegar',
      ctaReserve: 'Contactar a PIXAN',
      contact: 'PIXAN · +52 984 217 2816',
      mapsUrl: GOOGLE_MAPS_PLAYA,
      whatsappKey: 'reservePlaya',
    },
    {
      city: 'Cancún',
      venue: 'ABBA Massage Studio & Wellness Center',
      schedule: [
        { date: 'Sábado 14 de noviembre', detail: 'Consulta modalidad y horario por WhatsApp.' },
        { date: 'Domingo 15 de noviembre', detail: 'Consulta modalidad y horario por WhatsApp.' },
        { date: 'Lunes 16 de noviembre', detail: 'Consulta modalidad y horario por WhatsApp.' },
      ],
      ctaDirections: 'Cómo Llegar',
      ctaReserve: 'Contactar a ABBA Massage',
      contact: 'ABBA Massage · +52 998 303 2313',
      mapsUrl: GOOGLE_MAPS_CANCUN,
      whatsappKey: 'reserveCancun',
    },
  ],
  prices: [
    { title: 'Sesión grupal', regular: '$1,250', early: '$1,070 pronto pago', note: 'Incluye sound healing' },
    { title: 'Sesión individual', regular: '$1,350', early: '$1,150 pronto pago' },
    { title: 'Individual + masaje de 40 min', regular: '$1,600' },
  ],
  deposit: 'Reserva con 50%.',
  earlyPayment: 'Para conservar el precio de pronto pago, liquida la diferencia al menos 5 días antes de tu sesión.',
};

// =============================================================================
// ===== BOOKING SECTION =====
// =============================================================================

const BOOKING_CONTENT = {
  label: 'Reserva',
  title: 'Agenda tu Sesión',
  subtitle: 'Elige la modalidad que mejor se adapte a ti',
  cards: [
    { title: 'Sesión Individual', description: 'Presencial en Cancún', cta: 'Agendar', whatsappKey: 'bookingIndividual' },
    { title: 'Sesión en Línea', description: 'Desde cualquier lugar', cta: 'Agendar', whatsappKey: 'bookingOnline' },
    { title: 'Sesión Grupal', description: 'Próximas fechas', cta: 'Ver Fechas', whatsappKey: 'bookingGrupal' },
  ],
};

// =============================================================================
// ===== WHATSAPP CTA SECTION =====
// =============================================================================

const WHATSAPP_CTA_CONTENT = {
  title: '¿Aún tienes dudas?',
  description: 'No pasa nada. Decidir asistir a una Constelación es una decisión personal. Tal vez este no sea tu momento. Si todavía tienes dudas, puedes escribirnos. Con gusto responderemos tus preguntas para que decidas con tranquilidad.',
  ctaText: 'Contactar por WhatsApp',
  whatsappKey: 'general',
};

// =============================================================================
// ===== CLOSING CONTENT =====
// =============================================================================

const CLOSING_CONTENT = {
  title: 'No tienes que tener todas las respuestas para dar el primer paso',
  description: 'A veces, lo único que necesitamos es un espacio donde podamos ver nuestra historia con otros ojos. Si sientes que hay algo en tu vida que lleva tiempo pidiendo atención, estaremos aquí para acompañarte.',
  ctaText: 'Agenda una Sesión',
  whatsappKey: 'closing',
};

// =============================================================================
// ===== ANIMATION VARIANTS =====
// =============================================================================

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

// =============================================================================
// ===== ICON MAPPING =====
// =============================================================================

const getIcon = (iconName, className = "w-6 h-6") => {
  const icons = {
    heart: <Heart className={className} />,
    users: <Users className={className} />,
    sparkles: <Sparkles className={className} />,
    dollar: <DollarSign className={className} />,
    briefcase: <Briefcase className={className} />,
    scale: <Scale className={className} />,
    refresh: <RefreshCw className={className} />,
    video: <Video className={className} />,
    user: <UserCircle className={className} />,
    globe: <Globe className={className} />,
    headphones: <Headphones className={className} />,
    file: <FileText className={className} />,
    book: <BookOpen className={className} />,
  };
  return icons[iconName] || <Heart className={className} />;
};

// =============================================================================
// ===== AUDIO PLAYER COMPONENT =====
// =============================================================================

const AudioPlayer = ({ src, title }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(null);

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      const progress = (audioRef.current.currentTime / audioRef.current.duration) * 100;
      setProgress(progress);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setProgress(0);
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <div className="bg-[#FAFAFA] rounded-xl p-4">
      <audio
        ref={audioRef}
        src={src}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
      />
      <div className="flex items-center gap-4">
        <button
          onClick={togglePlay}
          className="w-12 h-12 rounded-full bg-[#A91B60] hover:bg-[#8A1650] text-white flex items-center justify-center transition-colors"
          aria-label={isPlaying ? 'Pausar' : 'Reproducir'}
        >
          {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
        </button>
        <div className="flex-1">
          <div className="flex items-center justify-between text-sm text-[#5A5A5A] mb-1">
            <span className="font-['Manrope']">{title}</span>
            <span className="font-['Manrope']">
              {formatTime(audioRef.current?.currentTime || 0)} / {formatTime(duration)}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-[#A91B60] h-2 rounded-full transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// ===== RESOURCE CARD COMPONENT =====
// =============================================================================

const getYouTubeEmbedUrl = (link) => {
  try {
    const url = new URL(link);
    const hostname = url.hostname.replace(/^www\./, '');
    let videoId = null;
    if (hostname === 'youtu.be') videoId = url.pathname.split('/')[1];
    if (hostname === 'youtube.com' || hostname === 'm.youtube.com') {
      videoId = url.searchParams.get('v') || url.pathname.match(/^\/(?:shorts|embed)\/([^/]+)/)?.[1];
    }
    return videoId && /^[a-zA-Z0-9_-]+$/.test(videoId)
      ? `https://www.youtube.com/embed/${videoId}`
      : link;
  } catch {
    return link;
  }
};

const ResourceCard = ({ resource }) => {
  const [isExpanded, setIsExpanded] = useState(
    () => new URLSearchParams(window.location.search).get('resource') === resource.id
  );
  const [shareStatus, setShareStatus] = useState('');

  const shareResource = async () => {
  const url = new URL('/constelaciones', window.location.origin);
    url.searchParams.set('resource', resource.id);
    const shareData = {
      title: resource.title,
      text: resource.description,
      url: url.toString(),
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(shareData.url);
        setShareStatus('Enlace copiado');
      }
    } catch (error) {
      if (error.name !== 'AbortError') setShareStatus('No se pudo compartir el enlace');
    }
  };
  
  if (resource.comingSoon) {
    return (
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 opacity-60">
        <div className="flex items-center gap-2 text-[#A91B60] mb-3">
          {getIcon(RESOURCE_CATEGORIES[resource.category]?.icon || 'video', 'w-5 h-5')}
          <span className="text-sm font-['Manrope'] font-medium">{RESOURCE_LIBRARY_CONTENT.comingSoonLabel}</span>
        </div>
        <h4 className="font-['Playfair_Display'] text-lg text-[#1C1C1C] mb-2">{resource.title}</h4>
        <p className="text-[#5A5A5A] font-['Manrope'] text-sm">{resource.description}</p>
      </div>
    );
  }

  return (
    <div id={`recurso-${resource.id}`} className="scroll-mt-24 bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-[#A91B60]">
          {getIcon(RESOURCE_CATEGORIES[resource.category]?.icon || 'video', 'w-5 h-5')}
          <span className="text-sm font-['Manrope'] font-medium capitalize">
            {RESOURCE_CATEGORIES[resource.category]?.label || resource.category}
          </span>
        </div>
        {resource.duration && (
          <span className="text-sm text-[#5A5A5A] font-['Manrope'] flex items-center gap-1">
            <Clock className="w-4 h-4" />
            {resource.duration}
          </span>
        )}
      </div>
      
      <h4 className="font-['Playfair_Display'] text-lg text-[#1C1C1C] mb-2">{resource.title}</h4>
      <p className="text-[#5A5A5A] font-['Manrope'] text-sm mb-4">{resource.description}</p>
      
      <div className="flex flex-wrap items-center gap-3 mb-3">
        {!isExpanded && (
          <Button
            onClick={() => setIsExpanded(true)}
            className="bg-[#A91B60] hover:bg-[#8A1650] text-white rounded-full px-6 py-2 font-['Manrope'] font-medium text-sm"
          >
            {resource.buttonText}
          </Button>
        )}
        <button
          type="button"
          onClick={shareResource}
          aria-label={`Compartir ${resource.title}`}
          className="inline-flex items-center gap-2 rounded-full border border-[#A91B60] px-4 py-2 text-sm font-['Manrope'] font-medium text-[#A91B60] hover:bg-[#A91B60]/10"
        >
          <Share2 className="w-4 h-4" aria-hidden="true" /> Compartir
        </button>
        {shareStatus && <span role="status" className="text-sm text-[#5A5A5A]">{shareStatus}</span>}
      </div>
      {isExpanded && (
        <div className="space-y-4">
          {resource.mediaType === 'audio' && (
            <AudioPlayer src={resource.mediaUrl} title={resource.title} />
          )}
          {resource.mediaType === 'video' && (
            <div className="rounded-xl overflow-hidden">
              <video
                src={resource.mediaUrl}
                controls
                className="w-full"
                poster={resource.thumbnail}
              />
            </div>
          )}
          {resource.mediaType === 'youtube' && (
            <div className="aspect-video rounded-xl overflow-hidden">
              <iframe
                src={getYouTubeEmbedUrl(resource.mediaUrl)}
                title={resource.title}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          )}
          {resource.mediaType === 'vimeo' && (
            <div className="aspect-video rounded-xl overflow-hidden">
              <iframe
                src={resource.mediaUrl}
                className="w-full h-full"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
              />
            </div>
          )}
          <button
            onClick={() => setIsExpanded(false)}
            className="text-[#5A5A5A] hover:text-[#A91B60] font-['Manrope'] text-sm underline"
          >
            Cerrar
          </button>
        </div>
      )}
    </div>
  );
};

// =============================================================================
// ===== MAIN COMPONENT =====
// =============================================================================


const CONY_VIDEO_GROUPS = [
  {
    "id": "session",
    "label": "Cómo es una sesión",
    "description": "Conoce cómo se desarrolla una constelación, en grupo o en línea."
  },
  {
    "id": "process",
    "label": "Antes y después",
    "description": "Cony explica la preparación y el tiempo para integrar lo vivido."
  },
  {
    "id": "questions",
    "label": "Dudas frecuentes",
    "description": "Reflexiones para entender el enfoque y sus alcances."
  }
];
const CONY_VIDEOS = [
  {
    "id": "ISUvISFcBqg",
    "group": "session",
    "title": "¿Cómo comienza una Constelación Familiar Grupal?",
    "thumbnail": "https://i.ytimg.com/vi/ISUvISFcBqg/sardefault.jpg?sqp=-oaymwEoCJUDENAFSFqQAgHyq4qpAxcIARUAAIhC2AEB4gEKCBgQAhgGOAFAAQ==&rs=AOn4CLDdG3tM3dJONNV4K1MVqpP4NeKnvw"
  },
  {
    "id": "y3p3AtC7OfE",
    "group": "session",
    "title": "¿Cómo es una constelación en línea?",
    "thumbnail": "https://i.ytimg.com/vi/y3p3AtC7OfE/sardefault.jpg?sqp=-oaymwEoCJUDENAFSFqQAgHyq4qpAxcIARUAAIhC2AEB4gEKCBgQAhgGOAFAAQ==&rs=AOn4CLB_NhIlB1xtcP3cFcgHzQoabolLew"
  },
  {
    "id": "UvW4DsOWwqc",
    "group": "process",
    "title": "¿Comienzas a constelar antes de tu sesión?",
    "thumbnail": "https://i.ytimg.com/vi/UvW4DsOWwqc/oar3.jpg?sqp=-oaymwEoCJUDENAFSFqQAgHyq4qpAxcIARUAAIhC2AEB4gEKCBgQAhgGOAFAAQ==&rs=AOn4CLCJlcJHy_L91hsoH6YXIyTywzySow"
  },
  {
    "id": "y4VyBCLZMUY",
    "group": "process",
    "title": "¿Por qué esperar antes de tomar decisiones?",
    "thumbnail": "https://i.ytimg.com/vi/y4VyBCLZMUY/sardefault.jpg?sqp=-oaymwEoCJUDENAFSFqQAgHyq4qpAxcIARUAAIhC2AEB4gEKCBgQAhgGOAFAAQ==&rs=AOn4CLA9RBUrbp_v8lDJdL3aUYVn8NsxVQ"
  },
  {
    "id": "OBx7Ou7YpUA",
    "group": "questions",
    "title": "¿Tus ancestros tienen la culpa de lo que te pasa?",
    "thumbnail": "https://i.ytimg.com/vi/OBx7Ou7YpUA/sardefault.jpg?sqp=-oaymwEoCJUDENAFSFqQAgHyq4qpAxcIARUAAIhC2AEB4gEKCBgQAhgGOAFAAQ==&rs=AOn4CLDolUZAG6w47UNym5kfDsEC2hFMtw"
  },
  {
    "id": "-msEPruIaBg",
    "group": "questions",
    "title": "¿Cuándo no funcionan las constelaciones?",
    "thumbnail": "https://i.ytimg.com/vi/-msEPruIaBg/sardefault.jpg?sqp=-oaymwEoCJUDENAFSFqQAgHyq4qpAxcIARUAAIhC2AEB4gEKCBgQAhgGOAFAAQ==&rs=AOn4CLDLJogscXq_6Q6FVrDK6spYyI9MAA"
  }
];

const ConyVideoLibrary = () => {
  const [groupId, setGroupId] = useState('session');
  const [selectedId, setSelectedId] = useState(CONY_VIDEOS[0].id);
  const [isPlaying, setIsPlaying] = useState(false);
  const group = CONY_VIDEO_GROUPS.find(item => item.id === groupId);
  const videos = CONY_VIDEOS.filter(item => item.group === groupId);
  const selected = videos.find(item => item.id === selectedId) || videos[0];
  const chooseGroup = id => {
    setGroupId(id);
    setSelectedId(CONY_VIDEOS.find(item => item.group === id).id);
    setIsPlaying(false);
  };
  const chooseVideo = id => {
    setSelectedId(id);
    setIsPlaying(false);
  };
  return (
    <section id="cony-videos" className="py-16 md:py-20 bg-[#F8F3F0] scroll-mt-24" aria-labelledby="cony-videos-title" data-testid="cony-video-library">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center max-w-2xl mx-auto mb-9">
          <p className="text-sm uppercase tracking-[0.2em] text-[#A91B60] font-['Manrope'] font-semibold mb-3">EN PALABRAS DE CONY</p>
          <h2 id="cony-videos-title" className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#454545] mb-4">Cony responde</h2>
          <p className="text-[#5A5A5A] font-['Manrope'] leading-relaxed">Elige un tema y escucha a Cony Trejo. Videos breves para conocer el proceso y resolver tus dudas, a tu ritmo.</p>
        </div>
        <div className="flex flex-wrap justify-center gap-2 md:gap-3 mb-9" role="group" aria-label="Temas de los videos">
          {CONY_VIDEO_GROUPS.map(item => (
            <button key={item.id} type="button" aria-pressed={groupId === item.id} onClick={() => chooseGroup(item.id)} className={`rounded-full border px-4 py-3 text-sm font-['Manrope'] font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#A91B60] ${groupId === item.id ? 'border-[#A91B60] bg-[#A91B60] text-white' : 'border-[#DCCEC8] bg-white text-[#5A5A5A] hover:border-[#A91B60] hover:text-[#A91B60]'}`}>{item.label}</button>
          ))}
        </div>
        <div className="grid md:grid-cols-[minmax(0,320px)_minmax(0,1fr)] gap-8 md:gap-12 items-start max-w-4xl mx-auto">
          <div className="w-full max-w-[280px] md:max-w-[320px] mx-auto">
            <div className="aspect-[9/16] rounded-2xl overflow-hidden bg-[#EEE3DC] shadow-md">
              {isPlaying ? (
                <iframe key={selected.id} src={`https://www.youtube.com/embed/${selected.id}?autoplay=1&rel=0`} title={selected.title} className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
              ) : (
                <button type="button" onClick={() => setIsPlaying(true)} aria-label={`Reproducir: ${selected.title}`} className="relative block w-full h-full group focus-visible:outline focus-visible:outline-4 focus-visible:outline-[#A91B60]">
                  <img src={selected.thumbnail} alt="" onError={(event) => { if (event.currentTarget.src !== IMAGES.cony) event.currentTarget.src = IMAGES.cony; }} loading="lazy" className="w-full h-full object-cover" />
                  <span className="absolute inset-0 flex items-center justify-center bg-[#454545]/10 group-hover:bg-[#454545]/20 transition-colors">
                    <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[#A91B60] text-white shadow-lg group-hover:scale-105 transition-transform"><Play className="w-7 h-7 ml-1" fill="currentColor" aria-hidden="true" /></span>
                  </span>
                </button>
              )}
            </div>
            <p className="font-['Manrope'] text-xs text-[#76706D] mt-3 text-center">Cony Trejo · Video de YouTube</p>
          </div>
          <div className="min-w-0">
            <p className="text-xs tracking-[0.15em] uppercase text-[#A91B60] font-semibold font-['Manrope'] mb-2">{videos.length} VIDEOS</p>
            <h3 className="font-['Playfair_Display'] text-2xl md:text-3xl text-[#454545] mb-3">{group.label}</h3>
            <p className="text-[#5A5A5A] font-['Manrope'] leading-relaxed mb-6">{group.description}</p>
            <div className="space-y-3">
              {videos.map((video, index) => (
                <button key={video.id} type="button" aria-pressed={selected.id === video.id} onClick={() => chooseVideo(video.id)} className={`w-full flex items-center gap-4 p-4 rounded-xl border text-left transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#A91B60] ${selected.id === video.id ? 'bg-white border-[#A91B60] shadow-sm' : 'bg-white/60 border-[#E4D9D3] hover:bg-white hover:border-[#A91B60]/50'}`}>
                  <span className="shrink-0 w-11 h-11 rounded-full bg-[#F4E6ED] text-[#A91B60] flex items-center justify-center font-['Manrope'] text-sm font-semibold">{String(index + 1).padStart(2, '0')}</span>
                  <span className="min-w-0 flex-1"><span className="block font-['Manrope'] font-semibold text-[#454545] text-sm md:text-base leading-relaxed">{video.title}</span><span className="block font-['Manrope'] text-xs text-[#A91B60] mt-1">{selected.id === video.id ? 'Seleccionado · Pulsa reproducir' : 'Seleccionar video'}</span></span>
                  <Play className="shrink-0 w-4 h-4 text-[#A91B60]" aria-hidden="true" />
                </button>
              ))}
            </div>
            <a href={YOUTUBE_CHANNEL + '/shorts'} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm text-[#A91B60] font-['Manrope'] font-semibold mt-7 hover:underline">Ver todos en YouTube <ArrowRight className="w-4 h-4" aria-hidden="true" /></a>
          </div>
        </div>
      </div>
    </section>
  );
};

export const FamilyConstellations = () => {
  const [openFAQ, setOpenFAQ] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');
  const bioRef = useRef(null);

  useEffect(() => {
    const resourceId = new URLSearchParams(window.location.search).get('resource');
    if (!RESOURCES.some((resource) => resource.visible && resource.id === resourceId)) return undefined;
    const timer = window.setTimeout(() => {
      document.getElementById(`recurso-${resourceId}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 300);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    const title = 'Constelaciones Familiares en Cancún | Cony Trejo';
    const description = 'Sesiones de Constelaciones Familiares con Cony Trejo en Cancún, Playa del Carmen y en línea. Modalidades individuales y grupales.';
    const canonicalUrl = 'https://abbamassage.com/constelaciones';
    const imageUrl = IMAGES.group;

    document.title = title;

    const setMeta = (attribute, key, value) => {
      let element = document.head.querySelector(`meta[${attribute}="${key}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, key);
        document.head.appendChild(element);
      }
      element.setAttribute('content', value);
    };

    setMeta('name', 'description', description);
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:type', 'website');
    setMeta('property', 'og:url', canonicalUrl);
    setMeta('property', 'og:image', imageUrl);
    setMeta('property', 'og:locale', 'es_MX');
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', description);
    setMeta('name', 'twitter:image', imageUrl);

    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', canonicalUrl);

    const schema = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Service',
          '@id': canonicalUrl + '#service',
          name: 'Constelaciones Familiares con Cony Trejo',
          description,
          serviceType: 'Constelaciones Familiares',
          provider: { '@type': 'Person', name: 'Cony Trejo' },
          areaServed: ['Cancún', 'Playa del Carmen', 'En línea'],
          url: canonicalUrl
        },
        {
          '@type': 'BreadcrumbList',
          '@id': canonicalUrl + '#breadcrumb',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Inicio', item: 'https://abbamassage.com/' },
            { '@type': 'ListItem', position: 2, name: 'Constelaciones Familiares', item: canonicalUrl }
          ]
        },
        {
          '@type': 'FAQPage',
          '@id': canonicalUrl + '#faq',
          mainEntity: FAQ_CONTENT.items.map((item) => ({
            '@type': 'Question',
            name: item.q,
            acceptedAnswer: { '@type': 'Answer', text: item.a }
          }))
        },
        ...['2026-11-14', '2026-11-15', '2026-11-16'].map((startDate) => ({
          '@type': 'Event',
          name: 'Constelaciones Familiares con Cony Trejo en ABBA Massage',
          startDate,
          description: 'Sesiones de Constelaciones Familiares. Consulta modalidades y horarios por WhatsApp.',
          eventStatus: 'https://schema.org/EventScheduled',
          eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
          url: canonicalUrl,
          location: {
            '@type': 'Place',
            name: 'ABBA Massage Studio & Wellness Center',
            address: {
              '@type': 'PostalAddress',
              streetAddress: 'Calle Playa Brava 26, Manzana 1, Lote 53',
              addressLocality: 'Cancún',
              addressRegion: 'Quintana Roo',
              postalCode: '77508',
              addressCountry: 'MX'
            }
          },
          organizer: { '@id': 'https://abbamassage.com/#business' },
          performer: { '@type': 'Person', name: 'Cony Trejo' }
        }))
      ]
    };

    const staticSchema = document.getElementById('constelaciones-static-schema');
    let schemaScript = null;
    if (!staticSchema) {
      schemaScript = document.head.querySelector('#constelaciones-schema');
      if (!schemaScript) {
        schemaScript = document.createElement('script');
        schemaScript.id = 'constelaciones-schema';
        schemaScript.type = 'application/ld+json';
        document.head.appendChild(schemaScript);
      }
      schemaScript.textContent = JSON.stringify(schema);
    }

    return () => {
      schemaScript?.remove();
    };
  }, []);

  const scrollToBio = () => {
    bioRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Filter resources by category
  const filteredResources = RESOURCES.filter(r => r.visible && (activeCategory === 'all' || r.category === activeCategory))
    .sort((a, b) => a.order - b.order);

  return (
    <div data-testid="constellations-page" className="bg-white">
      
      {/* 1. HERO SECTION */}
      <section className="relative min-h-[90vh] flex items-center pt-20" data-testid="hero">
        <div className="absolute inset-0 z-0">
          <img
            src={IMAGES.support}
            alt="Sesión de Constelaciones Familiares"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-16">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="max-w-2xl"
          >
            <motion.h1 variants={fadeInUp} className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl text-white mb-6 leading-tight">
              {HERO_CONTENT.headline}
            </motion.h1>
            <motion.p variants={fadeInUp} className="text-lg text-white/90 font-['Manrope'] mb-4 leading-relaxed">
              {HERO_CONTENT.subheadline}
            </motion.p>
            <motion.p variants={fadeInUp} className="text-lg text-white/80 font-['Manrope'] mb-8 leading-relaxed">
              {HERO_CONTENT.subheadline2}
            </motion.p>
            <motion.div variants={fadeInUp} className="flex flex-wrap gap-4">
              <a href={getWhatsAppUrl('heroMain')} target="_blank" rel="noopener noreferrer">
                <Button className="bg-[#A91B60] hover:bg-[#8A1650] text-white rounded-full px-8 py-6 font-['Manrope'] font-semibold">
                  {HERO_CONTENT.ctaPrimary}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </a>
              <a href="#what-are">
                <Button variant="outline" className="border-white text-white hover:bg-white hover:text-[#1C1C1C] rounded-full px-8 py-6 font-['Manrope'] font-semibold">
                  {HERO_CONTENT.ctaSecondary}
                </Button>
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <nav aria-label="Migas de pan" className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 text-sm font-['Manrope'] text-[#5A5A5A]">
          <Link to="/" className="hover:text-[#A91B60]">Inicio</Link>
          <span className="mx-2" aria-hidden="true">/</span>
          <span aria-current="page">Constelaciones Familiares</span>
        </div>
      </nav>

      <section className="py-12 bg-[#FAFAFA]" data-testid="constellations-answer">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl text-[#1C1C1C] mb-4">
            Constelaciones Familiares individuales, grupales y en línea
          </h2>
          <p className="text-lg text-[#5A5A5A] font-['Manrope'] leading-relaxed">
            Las Constelaciones Familiares son una práctica terapéutica complementaria para observar relaciones, patrones y situaciones desde una perspectiva sistémica. Cony Trejo facilita sesiones privadas y grupales en Cancún, Playa del Carmen y en línea.
          </p>
        </div>
      </section>

      {/* 2. WHAT ARE FAMILY CONSTELLATIONS */}
      <section id="what-are" className="py-20" data-testid="what-are">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-12"
          >
            <motion.p variants={fadeInUp} className="text-sm uppercase tracking-[0.2em] text-[#A91B60] mb-3 font-['Manrope'] font-semibold">
              {INTRODUCTION_CONTENT.label}
            </motion.p>
            <motion.h2 variants={fadeInUp} className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#1C1C1C] mb-6">
              {INTRODUCTION_CONTENT.title}
            </motion.h2>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              {INTRODUCTION_CONTENT.paragraphs.map((paragraph, index) => (
                <p key={index} className="text-lg text-[#5A5A5A] font-['Manrope'] mb-6 leading-relaxed">
                  {paragraph}
                </p>
              ))}
              <blockquote className="border-l-4 border-[#A91B60] pl-6 italic text-xl text-[#1C1C1C] font-['Playfair_Display']">
                {INTRODUCTION_CONTENT.quote}
              </blockquote>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-[#FAFAFA] rounded-2xl p-6 flex flex-col items-center justify-center"
            >
              <a href="#cony-videos" aria-label="Ver cómo comienza una constelación grupal" className="relative block w-full max-w-[240px] aspect-[9/16] rounded-xl overflow-hidden bg-[#EEE3DC] shadow-md group">
                <img src={CONY_VIDEOS[0].thumbnail} alt="Cony Trejo: cómo comienza una constelación grupal" onError={(event) => { if (event.currentTarget.src !== IMAGES.cony) event.currentTarget.src = IMAGES.cony; }} loading="lazy" className="w-full h-full object-cover" />
                <span className="absolute inset-0 flex items-center justify-center bg-[#454545]/10"><span className="w-14 h-14 rounded-full bg-[#A91B60] text-white flex items-center justify-center shadow-lg group-hover:scale-105 transition-transform"><Play className="w-6 h-6 ml-1" fill="currentColor" aria-hidden="true" /></span></span>
              </a>
              <h3 className="font-['Playfair_Display'] text-2xl text-[#454545] text-center mt-6 mb-2">¿Cómo comienza una Constelación Familiar Grupal?</h3>
              <p className="text-[#5A5A5A] font-['Manrope'] text-center mb-5">Escucha a Cony y explora los videos por tema.</p>
              <a href="#cony-videos" className="inline-flex items-center gap-2 border border-[#A91B60] text-[#A91B60] hover:bg-[#A91B60] hover:text-white rounded-full px-6 py-3 font-['Manrope'] font-semibold text-sm transition-colors"><Play className="w-4 h-4" aria-hidden="true" /> Ver videos por tema</a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 3. SITUATIONS */}
      <section className="py-20 bg-[#FAFAFA]" data-testid="situations">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-12"
          >
            <motion.p variants={fadeInUp} className="text-sm uppercase tracking-[0.2em] text-[#A91B60] mb-3 font-['Manrope'] font-semibold">
              {SITUATIONS_CONTENT.label}
            </motion.p>
            <motion.h2 variants={fadeInUp} className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#1C1C1C] mb-4">
              {SITUATIONS_CONTENT.title}
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-[#5A5A5A] font-['Manrope'] max-w-2xl mx-auto">
              {SITUATIONS_CONTENT.subtitle}
            </motion.p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8"
          >
            {SITUATIONS_CONTENT.items.map((item, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="bg-white rounded-xl p-5 text-center shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="w-12 h-12 rounded-full bg-[#A91B60]/10 flex items-center justify-center mx-auto mb-3">
                  {getIcon(item.icon, 'w-6 h-6 text-[#A91B60]')}
                </div>
                <p className="text-[#1C1C1C] font-['Manrope'] font-medium">{item.label}</p>
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <div className="flex flex-wrap justify-center gap-2 mb-6">
              {SITUATIONS_CONTENT.additionalItems.map((item, index) => (
                <span key={index} className="bg-white px-4 py-2 rounded-full text-[#5A5A5A] font-['Manrope'] text-sm">
                  {item}
                </span>
              ))}
            </div>
            <p className="text-[#5A5A5A] font-['Manrope'] italic max-w-2xl mx-auto">
              {SITUATIONS_CONTENT.note}
            </p>
          </motion.div>
        </div>
      </section>

      {/* 4. SESSION MODALITIES - THREE CARDS */}
      <section className="py-20" data-testid="session-modalities">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-12"
          >
            <motion.p variants={fadeInUp} className="text-sm uppercase tracking-[0.2em] text-[#A91B60] mb-3 font-['Manrope'] font-semibold">
              {SESSION_MODALITIES.label}
            </motion.p>
            <motion.h2 variants={fadeInUp} className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#1C1C1C]">
              {SESSION_MODALITIES.title}
            </motion.h2>
          </motion.div>

          <div className="grid lg:grid-cols-3 gap-8 mb-12">
            {SESSION_MODALITIES.cards.map((card, index) => (
              <motion.div
                key={card.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100 flex flex-col"
              >
                {card.badge && (
                  <div className="bg-[#A91B60] text-white text-center py-2 text-sm font-['Manrope'] font-medium">
                    <Globe className="w-4 h-4 inline mr-2" />
                    {card.badge}
                  </div>
                )}
                <div className="p-8 flex-1 flex flex-col">
                  <div className="w-14 h-14 rounded-full bg-[#A91B60]/10 flex items-center justify-center mb-4">
                    {getIcon(card.icon, 'w-7 h-7 text-[#A91B60]')}
                  </div>
                  
                  <h3 className="font-['Playfair_Display'] text-xl text-[#1C1C1C] mb-2">
                    {card.title}
                  </h3>
                  <p className="text-[#A91B60] font-['Manrope'] font-medium text-sm mb-4">
                    {card.subtitle}
                  </p>
                  
                  <div className="space-y-3 mb-6 flex-1">
                    {card.description.map((para, i) => (
                      <p key={i} className="text-[#5A5A5A] font-['Manrope'] text-sm leading-relaxed">
                        {para}
                      </p>
                    ))}
                  </div>
                  
                  <div className="border-t border-gray-100 pt-4 mt-auto">
                    <div className="flex items-center gap-2 text-[#5A5A5A] mb-3">
                      <Clock className="w-4 h-4" />
                      <span className="font-['Manrope'] text-sm">Duración aproximada: {card.duration}</span>
                    </div>
                    
                    {card.idealFor && (
                      <div className="mb-4">
                        <p className="font-['Manrope'] text-sm font-semibold text-[#1C1C1C] mb-2">Ideal para:</p>
                        <ul className="space-y-1">
                          {card.idealFor.map((item, i) => (
                            <li key={i} className="text-[#5A5A5A] font-['Manrope'] text-sm flex items-start gap-2">
                              <Check className="w-4 h-4 text-[#A91B60] mt-0.5 flex-shrink-0" />
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    <a href={getWhatsAppUrl(card.whatsappKey)} target="_blank" rel="noopener noreferrer" className="block">
                      <Button className="w-full bg-[#A91B60] hover:bg-[#8A1650] text-white rounded-full py-5 font-['Manrope'] font-semibold">
                        {card.buttonText}
                        <MessageCircle className="ml-2 w-4 h-4" />
                      </Button>
                    </a>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Help Choose CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center bg-[#FAFAFA] rounded-2xl p-8"
          >
            <h3 className="font-['Playfair_Display'] text-2xl text-[#1C1C1C] mb-3">
              {SESSION_MODALITIES.helpChoose.title}
            </h3>
            <p className="text-[#5A5A5A] font-['Manrope'] mb-6 max-w-xl mx-auto">
              {SESSION_MODALITIES.helpChoose.description}
            </p>
            <a href={getWhatsAppUrl(SESSION_MODALITIES.helpChoose.whatsappKey)} target="_blank" rel="noopener noreferrer">
              <Button className="bg-[#1C1C1C] hover:bg-[#333] text-white rounded-full px-8 py-5 font-['Manrope'] font-semibold">
                <MessageCircle className="mr-2 w-5 h-5" />
                {SESSION_MODALITIES.helpChoose.buttonText}
              </Button>
            </a>
          </motion.div>
        </div>
      </section>

      <ConyVideoLibrary />

      {/* 5. THERAPEUTIC APPROACH */}
      <section className="py-20 bg-[#FAFAFA]" data-testid="therapeutic-approach">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-8"
          >
            <motion.p variants={fadeInUp} className="text-sm uppercase tracking-[0.2em] text-[#A91B60] mb-3 font-['Manrope'] font-semibold">
              {THERAPEUTIC_APPROACH.label}
            </motion.p>
            <motion.h2 variants={fadeInUp} className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#1C1C1C] mb-6">
              {THERAPEUTIC_APPROACH.title}
            </motion.h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="space-y-4 mb-8"
          >
            {THERAPEUTIC_APPROACH.paragraphs.map((para, index) => (
              <p key={index} className="text-lg text-[#5A5A5A] font-['Manrope'] leading-relaxed text-center">
                {para}
              </p>
            ))}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white rounded-2xl p-6 border-l-4 border-[#A91B60]"
          >
            <p className="text-[#1C1C1C] font-['Manrope'] font-semibold text-center">
              {THERAPEUTIC_APPROACH.disclaimer}
            </p>
          </motion.div>
        </div>
      </section>

      {/* 6. MYTHS */}
      <section className="py-20" data-testid="myths">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-12"
          >
            <motion.p variants={fadeInUp} className="text-sm uppercase tracking-[0.2em] text-[#A91B60] mb-3 font-['Manrope'] font-semibold">
              {MYTHS_CONTENT.label}
            </motion.p>
            <motion.h2 variants={fadeInUp} className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#1C1C1C] mb-4">
              {MYTHS_CONTENT.title}
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-[#5A5A5A] font-['Manrope']">
              {MYTHS_CONTENT.subtitle}
            </motion.p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="space-y-6"
          >
            {MYTHS_CONTENT.items.map((item, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="bg-[#FAFAFA] rounded-2xl p-6 md:p-8"
              >
                <h4 className="text-lg font-['Manrope'] font-semibold text-[#1C1C1C] mb-3">
                  {item.question}
                </h4>
                <p className="text-[#5A5A5A] font-['Manrope'] leading-relaxed">
                  {item.answer}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* 7. IS THIS FOR YOU */}
      <section className="py-20 bg-[#A91B60]/5" data-testid="for-you">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-12"
          >
            <motion.p variants={fadeInUp} className="text-sm uppercase tracking-[0.2em] text-[#A91B60] mb-3 font-['Manrope'] font-semibold">
              {FOR_YOU_CONTENT.label}
            </motion.p>
            <motion.h2 variants={fadeInUp} className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#1C1C1C]">
              {FOR_YOU_CONTENT.title}
            </motion.h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid md:grid-cols-2 gap-4"
          >
            {FOR_YOU_CONTENT.items.map((item, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="bg-white rounded-xl p-5 flex items-start gap-4 shadow-sm"
              >
                <div className="w-8 h-8 rounded-full bg-[#A91B60]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check className="w-4 h-4 text-[#A91B60]" />
                </div>
                <p className="text-[#5A5A5A] font-['Manrope'] leading-relaxed">{item}</p>
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mt-10"
          >
            <a href={getWhatsAppUrl(FOR_YOU_CONTENT.whatsappKey)} target="_blank" rel="noopener noreferrer">
              <Button className="bg-[#A91B60] hover:bg-[#8A1650] text-white rounded-full px-8 py-6 font-['Manrope'] font-semibold">
                {FOR_YOU_CONTENT.ctaText}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </a>
          </motion.div>
        </div>
      </section>

      {/* 8. MEET CONY - SHORT */}
      <section className="py-20" data-testid="meet-cony">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-12"
          >
            <motion.p variants={fadeInUp} className="text-sm uppercase tracking-[0.2em] text-[#A91B60] mb-3 font-['Manrope'] font-semibold">
              {CONY_BIOGRAPHY.label}
            </motion.p>
            <motion.h2 variants={fadeInUp} className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#1C1C1C]">
              {CONY_BIOGRAPHY.title}
            </motion.h2>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-2 lg:order-1"
            >
              <p className="text-sm uppercase tracking-[0.15em] text-[#A91B60] mb-2 font-['Manrope'] font-semibold">
                {CONY_BIOGRAPHY.role}
              </p>
              <p className="text-lg text-[#5A5A5A] font-['Manrope'] mb-4 leading-relaxed">
                {CONY_BIOGRAPHY.shortBio}
              </p>
              <p className="text-lg text-[#5A5A5A] font-['Manrope'] mb-6 leading-relaxed">
                {CONY_BIOGRAPHY.philosophy}
              </p>
              <Button
                onClick={scrollToBio}
                variant="outline"
                className="border-[#A91B60] text-[#A91B60] hover:bg-[#A91B60] hover:text-white rounded-full px-6 py-5 font-['Manrope'] font-semibold"
              >
                {CONY_BIOGRAPHY.ctaText}
                <ChevronDown className="ml-2 w-4 h-4" />
              </Button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-1 lg:order-2"
            >
              <div className="relative">
                <img
                  src={IMAGES.cony}
                  alt="Cony Trejo - Consteladora Familiar"
                  className="rounded-2xl shadow-xl w-full max-w-md mx-auto"
                />
                <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-[#A91B60]/10 rounded-full -z-10" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 9. EXPANDED BIOGRAPHY */}
      <section ref={bioRef} id={CONY_BIOGRAPHY.expandedBio.sectionId} className="py-20 bg-[#FAFAFA]" data-testid="cony-bio-expanded">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.h2 variants={fadeInUp} className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#1C1C1C] text-center mb-12">
              {CONY_BIOGRAPHY.expandedBio.title}
            </motion.h2>

            <div className="space-y-10">
              {CONY_BIOGRAPHY.expandedBio.sections.map((section, sectionIndex) => (
                <motion.div
                  key={sectionIndex}
                  variants={fadeInUp}
                  className="bg-white rounded-2xl p-8"
                >
                  <h3 className="font-['Playfair_Display'] text-xl text-[#A91B60] mb-4">
                    {section.title}
                  </h3>
                  <div className="space-y-4">
                    {section.paragraphs.map((para, paraIndex) => (
                      <p key={paraIndex} className="text-[#5A5A5A] font-['Manrope'] leading-relaxed">
                        {para}
                      </p>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* 10. RESOURCE LIBRARY */}
      <section className="py-20" data-testid="resource-library">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-12"
          >
            <motion.p variants={fadeInUp} className="text-sm uppercase tracking-[0.2em] text-[#A91B60] mb-3 font-['Manrope'] font-semibold">
              {RESOURCE_LIBRARY_CONTENT.label}
            </motion.p>
            <motion.h2 variants={fadeInUp} className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#1C1C1C] mb-4">
              {RESOURCE_LIBRARY_CONTENT.title}
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-[#5A5A5A] font-['Manrope'] max-w-2xl mx-auto">
              {RESOURCE_LIBRARY_CONTENT.subtitle}
            </motion.p>
          </motion.div>

          {/* Category Filters */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex flex-wrap justify-center gap-3 mb-10"
          >
            {Object.values(RESOURCE_CATEGORIES).map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`px-5 py-2 rounded-full font-['Manrope'] font-medium text-sm transition-colors ${
                  activeCategory === cat.id
                    ? 'bg-[#A91B60] text-white'
                    : 'bg-[#FAFAFA] text-[#5A5A5A] hover:bg-[#A91B60]/10'
                }`}
              >
                {cat.icon && getIcon(cat.icon, 'w-4 h-4 inline mr-2')}
                {cat.label}
              </button>
            ))}
          </motion.div>

          {/* Resources Grid */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeCategory}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {filteredResources.length > 0 ? (
                filteredResources.map((resource) => (
                  <ResourceCard key={resource.id} resource={resource} />
                ))
              ) : (
                <div className="col-span-full text-center py-12">
                  <p className="text-[#5A5A5A] font-['Manrope']">{RESOURCE_LIBRARY_CONTENT.emptyState}</p>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* 11. FAQ */}
      <section className="py-20 bg-[#FAFAFA]" data-testid="faq">
        <div className="max-w-3xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-12"
          >
            <motion.p variants={fadeInUp} className="text-sm uppercase tracking-[0.2em] text-[#A91B60] mb-3 font-['Manrope'] font-semibold">
              {FAQ_CONTENT.label}
            </motion.p>
            <motion.h2 variants={fadeInUp} className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#1C1C1C]">
              {FAQ_CONTENT.title}
            </motion.h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="space-y-4"
          >
            {FAQ_CONTENT.items.map((item, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="bg-white rounded-xl overflow-hidden"
              >
                <button
                  onClick={() => setOpenFAQ(openFAQ === index ? null : index)}
                  className="w-full px-6 py-5 flex items-center justify-between text-left"
                >
                  <span className="font-['Manrope'] font-semibold text-[#1C1C1C] pr-4">{item.q}</span>
                  {openFAQ === index ? (
                    <ChevronUp className="w-5 h-5 text-[#A91B60] flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-[#A91B60] flex-shrink-0" />
                  )}
                </button>
                <AnimatePresence>
                  {openFAQ === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <p className="px-6 pb-5 text-[#5A5A5A] font-['Manrope'] leading-relaxed">
                        {item.a}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* 13. UPCOMING SESSIONS */}
      <section className="py-20 bg-[#FAFAFA]" data-testid="upcoming">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-12"
          >
            <motion.p variants={fadeInUp} className="text-sm uppercase tracking-[0.2em] text-[#A91B60] mb-3 font-['Manrope'] font-semibold">
              {UPCOMING_SESSIONS.label}
            </motion.p>
            <motion.h2 variants={fadeInUp} className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#1C1C1C] mb-4">
              {UPCOMING_SESSIONS.title}
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-[#5A5A5A] font-['Manrope'] max-w-2xl mx-auto">
              {UPCOMING_SESSIONS.intro}
            </motion.p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {UPCOMING_SESSIONS.locations.map((location, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl p-8 shadow-sm"
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 rounded-full bg-[#A91B60]/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-[#A91B60]" />
                  </div>
                  <div>
                    <h3 className="font-['Playfair_Display'] text-xl text-[#1C1C1C]">{location.city}</h3>
                    <p className="text-[#5A5A5A] font-['Manrope'] text-sm">{location.venue}</p>
                  </div>
                </div>
                <div className="space-y-3 mb-6">
                  {location.schedule.map((session, sessionIndex) => (
                    <div key={sessionIndex} className="flex items-start gap-3 text-[#5A5A5A]">
                      <Calendar className="w-4 h-4 mt-1 flex-shrink-0 text-[#A91B60]" />
                      <div>
                        <p className="font-['Manrope'] text-sm font-semibold text-[#1C1C1C]">{session.date}</p>
                        <p className="font-['Manrope'] text-sm">{session.detail}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-[#5A5A5A] font-['Manrope'] text-sm mb-4">{location.contact}</p>
                <div className="flex flex-wrap gap-3">
                  {location.mapsUrl && (
                    <a href={location.mapsUrl} target="_blank" rel="noopener noreferrer">
                      <Button variant="outline" className="border-[#1C1C1C] text-[#1C1C1C] hover:bg-[#1C1C1C] hover:text-white rounded-full px-5 py-2 font-['Manrope'] text-sm">
                        {location.ctaDirections}
                      </Button>
                    </a>
                  )}
                  <a href={getWhatsAppUrl(location.whatsappKey)} target="_blank" rel="noopener noreferrer">
                    <Button className="bg-[#A91B60] hover:bg-[#8A1650] text-white rounded-full px-5 py-2 font-['Manrope'] text-sm">
                      {location.ctaReserve}
                    </Button>
                  </a>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="mb-12"
          >
            <div className="grid md:grid-cols-3 gap-4 mb-6">
              {UPCOMING_SESSIONS.prices.map((price, index) => (
                <motion.div key={index} variants={fadeInUp} className="bg-white rounded-2xl p-6 text-center shadow-sm">
                  <h3 className="font-['Playfair_Display'] text-lg text-[#1C1C1C] mb-3">{price.title}</h3>
                  <p className="text-2xl font-semibold text-[#A91B60] font-['Manrope']">{price.regular}</p>
                  {price.early && <p className="text-[#1C1C1C] font-['Manrope'] font-semibold mt-2">{price.early}</p>}
                  {price.note && <p className="text-sm text-[#5A5A5A] font-['Manrope'] mt-2">{price.note}</p>}
                </motion.div>
              ))}
            </div>
            <div className="bg-[#A91B60]/10 rounded-2xl p-5 text-center">
              <p className="font-['Manrope'] font-semibold text-[#1C1C1C]">{UPCOMING_SESSIONS.deposit}</p>
              <p className="font-['Manrope'] text-sm text-[#5A5A5A] mt-1">{UPCOMING_SESSIONS.earlyPayment}</p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <p className="text-[#1C1C1C] font-['Playfair_Display'] text-xl mb-2">{UPCOMING_SESSIONS.alternative}</p>
            <p className="text-[#5A5A5A] font-['Manrope'] mb-6">{UPCOMING_SESSIONS.alternativeSubtitle}</p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href={getWhatsAppUrl('bookingIndividual')} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="border-[#A91B60] text-[#A91B60] hover:bg-[#A91B60] hover:text-white rounded-full px-6 py-4 font-['Manrope']">
                  Sesión Individual
                </Button>
              </a>
              <a href={getWhatsAppUrl('bookingOnline')} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="border-[#A91B60] text-[#A91B60] hover:bg-[#A91B60] hover:text-white rounded-full px-6 py-4 font-['Manrope']">
                  Sesión en Línea
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 14. BOOKING CARDS */}
      <section className="py-20" data-testid="booking">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-12"
          >
            <motion.p variants={fadeInUp} className="text-sm uppercase tracking-[0.2em] text-[#A91B60] mb-3 font-['Manrope'] font-semibold">
              {BOOKING_CONTENT.label}
            </motion.p>
            <motion.h2 variants={fadeInUp} className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#1C1C1C] mb-4">
              {BOOKING_CONTENT.title}
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-[#5A5A5A] font-['Manrope']">
              {BOOKING_CONTENT.subtitle}
            </motion.p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {BOOKING_CONTENT.cards.map((card, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-[#FAFAFA] rounded-2xl p-8 text-center"
              >
                <h3 className="font-['Playfair_Display'] text-xl text-[#1C1C1C] mb-2">{card.title}</h3>
                <p className="text-[#5A5A5A] font-['Manrope'] mb-6">{card.description}</p>
                <a href={getWhatsAppUrl(card.whatsappKey)} target="_blank" rel="noopener noreferrer">
                  <Button className="bg-[#A91B60] hover:bg-[#8A1650] text-white rounded-full px-8 py-4 font-['Manrope'] font-semibold">
                    {card.cta}
                    <MessageCircle className="ml-2 w-4 h-4" />
                  </Button>
                </a>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 15. WHATSAPP CTA */}
      <section className="py-20 bg-[#FAFAFA]" data-testid="whatsapp-cta">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#1C1C1C] mb-4">
              {WHATSAPP_CTA_CONTENT.title}
            </h2>
            <p className="text-[#5A5A5A] font-['Manrope'] mb-8 leading-relaxed">
              {WHATSAPP_CTA_CONTENT.description}
            </p>
            <a href={getWhatsAppUrl(WHATSAPP_CTA_CONTENT.whatsappKey)} target="_blank" rel="noopener noreferrer">
              <Button className="bg-[#25D366] hover:bg-[#128C7E] text-white rounded-full px-8 py-6 font-['Manrope'] font-semibold">
                <MessageCircle className="mr-2 w-5 h-5" />
                {WHATSAPP_CTA_CONTENT.ctaText}
              </Button>
            </a>
          </motion.div>
        </div>
      </section>

      {/* 16. CLOSING */}
      <section className="py-20 bg-[#1C1C1C]" data-testid="closing">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl text-white mb-6">
              {CLOSING_CONTENT.title}
            </h2>
            <p className="text-white/80 font-['Manrope'] mb-8 leading-relaxed">
              {CLOSING_CONTENT.description}
            </p>
            <a href={getWhatsAppUrl(CLOSING_CONTENT.whatsappKey)} target="_blank" rel="noopener noreferrer">
              <Button className="bg-[#A91B60] hover:bg-[#8A1650] text-white rounded-full px-8 py-6 font-['Manrope'] font-semibold">
                {CLOSING_CONTENT.ctaText}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default FamilyConstellations;
